import axios from 'axios';
import { ApiResponse, Department, RobotSettings, PromptFile, Company, SyncSettings, BackupStatus, BackupArchive, BackupSettingsData } from '../types';
import { getApiToken } from '../utils/auth';

const API_BASE_URL = '/api';
const API_LONG_TIMEOUT_MS = 3_600_000;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_LONG_TIMEOUT_MS,
});

api.interceptors.request.use((config) => {
  const token = getApiToken();
  if (token) {
    config.headers['X-API-Token'] = token;
  }
  // JWT пользователя хаба (plain <a href> его не отправляет — нужен для скачивания файлов)
  if (typeof window !== 'undefined') {
    const jwt = localStorage.getItem('token');
    if (jwt && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${jwt}`;
    }
  }
  return config;
});

export const departmentsApi = {
  list: async (companyId?: string): Promise<Department[]> => {
    const response = await api.get<ApiResponse<Department[]>>('/departments', {
      params: companyId ? { company_id: companyId } : undefined,
    });
    return response.data.data || [];
  },
  create: async (payload: Partial<Department> & { site_host: string; city_nom: string }): Promise<Department> => {
    const response = await api.post<ApiResponse<Department>>('/departments', payload);
    return response.data.data!;
  },
  update: async (id: string, payload: Partial<Department>): Promise<Department> => {
    const response = await api.put<ApiResponse<Department>>(`/departments/${id}`, payload);
    return response.data.data!;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/departments/${id}`);
  },
  replaceContactsInCategorySeo: async (
    id: string,
    body: {
      old_address?: string;
      old_phones?: string;
      new_address?: string | null;
      new_phones?: string | null;
      dry_run?: boolean;
    }
  ): Promise<{
    site_host: string;
    scanned_with_text: number;
    updated_documents: number;
    replacement_count: number;
    dry_run: boolean;
    examples: { category_id: string; replacements: number }[];
  }> => {
    const response = await api.post<ApiResponse<{
      site_host: string;
      scanned_with_text: number;
      updated_documents: number;
      replacement_count: number;
      dry_run: boolean;
      examples: { category_id: string; replacements: number }[];
    }>>(`/departments/${id}/replace-contacts-in-category-seo`, body);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Ошибка замены контактов');
    }
    return response.data.data;
  },

  /** Обновить карточку данными из ЕСМ (1С) по esm_id: падежи города, телефоны, адрес, режим, домен. */
  syncEsm: async (
    id: string
  ): Promise<{ department: Department; changed_fields: string[]; esm_id: string }> => {
    const response = await api.post<ApiResponse<{ department: Department; changed_fields: string[]; esm_id: string }>>(
      `/departments/${id}/sync-esm`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Ошибка синхронизации с ЕСМ');
    }
    return response.data.data;
  },

  /** Список департаментов из ЕСМ для выбора при импорте. */
  esmList: async (): Promise<
    {
      esm_id: string;
      name: string;
      domain: string;
      parent: string;
      name_for_site: string;
      phones: string;
      work_time: string;
      address: string;
      already_imported: boolean;
    }[]
  > => {
    const response = await api.get<ApiResponse<any[]>>('/departments/esm/list');
    if (!response.data.success) throw new Error(response.data.error || 'ЕСМ недоступен');
    return response.data.data || [];
  },

  /** Массовый импорт департаментов из ЕСМ по списку esm_id. */
  esmImport: async (
    esmIds: string[],
    companyId?: string,
    overwriteExisting = true
  ): Promise<{ created: string[]; updated: string[]; skipped: { esm_id: string; reason: string }[] }> => {
    const response = await api.post<ApiResponse<any>>('/departments/esm/import', {
      esm_ids: esmIds,
      company_id: companyId || null,
      overwrite_existing: overwriteExisting,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Ошибка импорта из ЕСМ');
    }
    return response.data.data;
  },
};

export const promptsApi = {
  getPrompts: async (): Promise<PromptFile[]> => {
    const response = await api.get<ApiResponse<PromptFile[]>>('/prompts');
    return response.data.data || [];
  },
  updatePrompt: async (name: string, content: string): Promise<void> => {
    await api.put(`/prompts/${name}`, { content });
  },
  getPromptContent: async (name: string): Promise<string> => {
    const response = await api.get<ApiResponse<string>>(`/prompts/${name}/content`);
    return response.data.data || '';
  },
};

function robotSettingsApiFor(type: 'brands' | 'categories' | 'tree') {
  return {
    getRobotSettings: async (): Promise<RobotSettings> => {
      const response = await api.get<ApiResponse<RobotSettings>>(`/robot/settings/${type}`);
      return response.data.data!;
    },
    updateRobotSettings: async (settings: RobotSettings): Promise<RobotSettings> => {
      const response = await api.put<ApiResponse<RobotSettings>>(`/robot/settings/${type}`, settings);
      return response.data.data!;
    },
  };
}

export const brandsApi = robotSettingsApiFor('brands');
export const categoriesApi = robotSettingsApiFor('categories');
export const treeApi = robotSettingsApiFor('tree');

export const companiesApi = {
  list: async (): Promise<Company[]> => {
    const response = await api.get<ApiResponse<Company[]>>('/companies');
    return response.data.data || [];
  },
  getActive: async (): Promise<Company | null> => {
    const response = await api.get<ApiResponse<Company>>('/companies/active');
    return response.data.data || null;
  },
  create: async (payload: Partial<Company> & { name: string }): Promise<Company> => {
    const response = await api.post<ApiResponse<Company>>('/companies', payload);
    return response.data.data!;
  },
  update: async (id: string, payload: Partial<Company>): Promise<Company> => {
    const response = await api.put<ApiResponse<Company>>(`/companies/${id}`, payload);
    return response.data.data!;
  },
  remove: async (id: string): Promise<void> => {
    await api.delete(`/companies/${id}`);
  },
  activate: async (id: string): Promise<Company> => {
    const response = await api.post<ApiResponse<Company>>(`/companies/${id}/activate`);
    return response.data.data!;
  },
};

export const syncSettingsApi = {
  get: async (): Promise<SyncSettings> => {
    const response = await api.get<ApiResponse<SyncSettings>>('/sync-settings');
    return response.data.data || { hour: 8, minute: 0 };
  },
  update: async (payload: Partial<SyncSettings>): Promise<SyncSettings> => {
    const response = await api.put<ApiResponse<SyncSettings>>('/sync-settings', payload);
    return response.data.data!;
  },
};

export interface UnifiedSyncStatus {
  in_progress: boolean;
  last_result: {
    status: string;
    results: Record<string, any>;
    errors: string[];
    timestamp: string;
  } | null;
}

export const unifiedSyncApi = {
  start: async (forceFull: boolean): Promise<void> => {
    const response = await api.post<ApiResponse<{ message: string; in_progress: boolean }>>(
      '/sync/all',
      null,
      { params: { force_full: forceFull } }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Не удалось запустить синхронизацию');
    }
  },
  status: async (): Promise<UnifiedSyncStatus> => {
    const response = await api.get<ApiResponse<UnifiedSyncStatus>>('/sync/all/status');
    return response.data.data || { in_progress: false, last_result: null };
  },
};

export default api;

/** Резервное копирование MongoDB: архивы лежат на сервере, Redis не архивируется */
export const backupApi = {
  getStatus: async (): Promise<BackupStatus> => {
    const response = await api.get<ApiResponse<BackupStatus>>('/backup/status');
    return response.data.data!;
  },
  list: async (): Promise<BackupArchive[]> => {
    const response = await api.get<ApiResponse<BackupArchive[]>>('/backup/list');
    return response.data.data || [];
  },
  // Архивация идёт синхронно и на больших базах занимает минуты — таймаут увеличен
  create: async (): Promise<{ success: boolean; data: any }> => {
    const response = await api.post('/backup/create', {}, { timeout: 3_600_000 });
    return response.data;
  },
  saveSettings: async (settings: Partial<BackupSettingsData>): Promise<BackupSettingsData> => {
    const response = await api.put<ApiResponse<BackupSettingsData>>('/backup/settings', settings);
    return response.data.data!;
  },
  remove: async (file: string): Promise<void> => {
    await api.delete(`/backup/${encodeURIComponent(file)}`);
  },
  restore: async (file: string, confirm: string): Promise<void> => {
    await api.post('/backup/restore', { file, confirm, drop: true }, { timeout: 3_600_000 });
  },
  /** Скачивание с заголовками авторизации (прямая ссылка без токена даёт 401). */
  download: async (file: string): Promise<void> => {
    const response = await api.get(`/backup/download/${encodeURIComponent(file)}`, {
      responseType: 'blob',
      timeout: 3_600_000,
    });
    const blob = response.data as Blob;
    if (blob.type.includes('json') || blob.size < 512) {
      const text = await blob.text();
      try {
        const err = JSON.parse(text) as { detail?: string; error?: string };
        throw new Error(err.detail || err.error || 'Не удалось скачать архив');
      } catch (e) {
        if (e instanceof Error && e.message !== 'Не удалось скачать архив') {
          throw e;
        }
      }
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file;
    link.click();
    URL.revokeObjectURL(url);
  },
};
